import * as XLSX from "xlsx";

const COL_CONTACT = "Customer Contact";
const COL_EMAIL = "Email Address";
const COL_STATUS = "Status";

/**
 * Downloads a valid example xlsx template for the "Uploaded Member List"
 * campaign template's target member upload. Shares the same column format as
 * the exclude member upload — see utils/excludeMember.ts for the parser.
 */
export const downloadTargetMemberTemplate = () => {
  const ws = XLSX.utils.aoa_to_sheet([
    [COL_CONTACT, COL_EMAIL],
    ["6591234567", "example@email.com"],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, "ExampleFormatTargetMembers.xlsx");
};

export type MatchedMemberRecord = {
  ContactIdd?: string;
  ContactNumber?: string;
  Email?: string;
};

const normalizePhone = (v?: string) => (v ?? "").replace(/\D/g, "");
const normalizeEmail = (v?: string) => (v ?? "").trim().toLowerCase();

/** Digit-suffix comparison so country-code prefix differences don't cause false negatives. */
const phoneMatches = (a: string, b: string) => {
  if (!a || !b) return false;
  if (a === b) return true;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;
  return shorter.length >= 6 && longer.endsWith(shorter);
};

export const TARGET_MEMBER_FOUND = "Found";
export const TARGET_MEMBER_NOT_FOUND = "Not Found";

/**
 * Best-effort match of uploaded target rows against the members the backend
 * confirmed exist (EstimatedTotalMember's ListMember), flagging each row as
 * Found/Not Found for the preview table. Heuristic because the API returns
 * matched member records, not a row-by-row verdict.
 */
export const annotateTargetMemberMatchStatus = (
  rows: Record<string, any>[],
  matchedMembers: MatchedMemberRecord[],
): Record<string, any>[] => {
  const matchedPhones = matchedMembers
    .map((m) => normalizePhone(`${m.ContactIdd ?? ""}${m.ContactNumber ?? ""}`))
    .filter(Boolean);
  const matchedEmails = new Set(
    matchedMembers.map((m) => normalizeEmail(m.Email)).filter(Boolean),
  );

  return rows.map((row) => {
    const phone = normalizePhone(row[COL_CONTACT]);
    const email = normalizeEmail(row[COL_EMAIL]);
    const isMatched =
      (!!email && matchedEmails.has(email)) ||
      (!!phone && matchedPhones.some((p) => phoneMatches(phone, p)));
    return {
      ...row,
      [COL_STATUS]: isMatched ? TARGET_MEMBER_FOUND : TARGET_MEMBER_NOT_FOUND,
    };
  });
};
